import { useEffect, useState, type CSSProperties } from "react";
import type { Room } from "colyseus.js";
import type { GlobalState, NotificationEvent, NotificationKind } from "@game/types";

interface Toast {
  id: number;
  kind: NotificationKind;
  text: string;
  spawnedAt: number;
}

const wrapStyle: CSSProperties = {
  position: "fixed",
  top: 16,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  flexDirection: "column",
  gap: 6,
  zIndex: 1200,
  pointerEvents: "none",
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
};

const KIND_ICON: Record<NotificationKind, string> = {
  friend_request: "👥",
  friend_accepted: "✓",
  mail_received: "✉",
  achievement_unlocked: "🏆",
  title_unlocked: "★",
  trade_invite: "↔",
  party_invite: "⚑",
  guild_invite: "⚔",
  tamer_level_up: "↑",
};

const KIND_COLOR: Record<NotificationKind, string> = {
  friend_request: "#7dd3fc",
  friend_accepted: "#86efac",
  mail_received: "#facc15",
  achievement_unlocked: "#fbbf24",
  title_unlocked: "#a855f7",
  trade_invite: "#60a5fa",
  party_invite: "#86efac",
  guild_invite: "#f472b6",
  tamer_level_up: "#fbbf24",
};

interface Props {
  globalRoom: Room<GlobalState> | null;
  /** ms a toast stays before fading. Default 5000. */
  visibleMs?: number;
}

let nextId = 1;

export function NotificationToaster({ globalRoom, visibleMs = 5000 }: Props) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    if (!globalRoom) return;
    const off = globalRoom.onMessage("notification", (n: NotificationEvent) => {
      const text = formatNotification(n);
      setToasts((prev) => [...prev, { id: nextId++, kind: n.kind, text, spawnedAt: Date.now() }]);
    });
    return () => {
      off();
    };
  }, [globalRoom]);

  // Sweep expired
  useEffect(() => {
    const id = window.setInterval(() => {
      const now = Date.now();
      setToasts((prev) => prev.filter((t) => now - t.spawnedAt < visibleMs));
    }, 500);
    return () => window.clearInterval(id);
  }, [visibleMs]);

  if (toasts.length === 0) return null;

  return (
    <div style={wrapStyle}>
      {toasts.map((t) => (
        <div
          key={t.id}
          style={{
            background: "rgba(15,18,24,0.92)",
            border: `1px solid ${KIND_COLOR[t.kind]}88`,
            borderLeft: `3px solid ${KIND_COLOR[t.kind]}`,
            color: "#e6e8ee",
            padding: "6px 10px",
            borderRadius: 6,
            fontSize: 12,
            display: "flex",
            gap: 8,
            alignItems: "center",
            boxShadow: "0 6px 18px rgba(0,0,0,0.4)",
          }}
        >
          <span style={{ color: KIND_COLOR[t.kind] }}>{KIND_ICON[t.kind]}</span>
          <span>{t.text}</span>
        </div>
      ))}
    </div>
  );
}

function formatNotification(n: NotificationEvent): string {
  const p = n.payload as Record<string, unknown>;
  switch (n.kind) {
    case "friend_request":
      return `Friend request from ${p.fromName ?? "someone"}`;
    case "friend_accepted":
      return `${p.byName ?? "Someone"} accepted your friend request`;
    case "mail_received":
      return `New mail: ${p.subject ?? p.itemName ?? "(item)"}`;
    case "achievement_unlocked":
      return `Achievement unlocked: ${p.name ?? p.achievementId}`;
    case "title_unlocked":
      return `Title unlocked: ${p.name ?? p.titleId}`;
    case "trade_invite":
      return `Trade invite from ${p.fromName ?? "someone"}`;
    case "party_invite":
      return `Party invite from ${p.fromName ?? "someone"}`;
    case "guild_invite":
      return `Guild invite: ${p.guildName ?? p.guildId}`;
    case "tamer_level_up":
      return `Level up! Tamer Lv ${p.newLevel ?? "?"}`;
  }
}
